import React, { useState, useEffect } from 'react';
import "./HistoricoCompra.css";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  Grid,
  Divider
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import dayjs from 'dayjs';
import api from '../../../services/api';
import API_CONFIG from '../../../config/api.config';



const HistoricoCompra = () => {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  // Estados para os filtros
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [fornecedorSelecionado, setFornecedorSelecionado] = useState('');
  const [comprasFiltradas, setComprasFiltradas] = useState([]);

  // Lista de fornecedores únicos para o filtro
  const fornecedores = [...new Set(compras.map(compra => compra.fornecedor))];

  // Monitora mudanças no menu
  useEffect(() => {
    const handleMenuChange = (event) => {
      setIsMenuExpanded(!event.detail.isCollapsed);
    };

    window.addEventListener('menuStateChange', handleMenuChange);
    return () => window.removeEventListener('menuStateChange', handleMenuChange);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Buscar compras da API
  const buscarCompras = async () => {
    try {
      setLoading(true);
      const idPrefeitura = localStorage.getItem('idPrefeitura');
      //console.log('ID prefeitura', idPrefeitura);

      if (!idPrefeitura) {
        //console.error('ID da prefeitura não encontrado');
        setLoading(false);
        return;
      }

      const params = {
        page: paginaAtual,
        PRE_ID: idPrefeitura,
        data_inicio: dataInicio,
        data_fim: dataFim
      };

      // Adiciona filtros de data apenas se estiverem preenchidos
      if (dataInicio) {
        params.data_inicio = dataInicio;
      }
      if (dataFim) {
        params.data_fim = dataFim;
      }

      const response = await api.get(API_CONFIG.ENDPOINTS.COMPRAS.LISTAR_HISTORICO_COMPRAS_PRE, {
        params
      });

      //console.log('Resposta da API:', response.data);

      if (response.data.status && response.data.compras) {
        const fetchedCompras = response.data.compras.data || [];
        setCompras(fetchedCompras);
        setTotalPaginas(response.data.compras.last_page || 1);
        // Aplica o filtro de fornecedor inicial
        aplicarFiltroFornecedor(fetchedCompras, fornecedorSelecionado);
      } else {
        setCompras([]);
        aplicarFiltroFornecedor([], '');
      }
    } catch (error) {
      //console.error('Erro ao buscar compras:', error);
      setCompras([]);
      aplicarFiltroFornecedor([], '');
    } finally {
      setLoading(false);
    }
  };

  // Função para aplicar filtro de fornecedor localmente
  const aplicarFiltroFornecedor = (listaCompras, fornecedor) => {
    if (!fornecedor) {
      setComprasFiltradas(listaCompras);
    } else {
      const filtradas = listaCompras.filter(compra =>
        compra.usuario && compra.usuario.USU_NOME &&
        compra.usuario.USU_NOME.toLowerCase().includes(fornecedor.toLowerCase())
      );
      setComprasFiltradas(filtradas);
    }
  };

  // Buscar compras ao montar componente e quando mudar a página ou datas
  useEffect(() => {
    buscarCompras();
  }, [paginaAtual, dataInicio, dataFim]); // Dependências incluem as datas

  // Aplicar filtro de fornecedor quando 'compras' ou 'fornecedorSelecionado' mudar
  useEffect(() => {
    aplicarFiltroFornecedor(compras, fornecedorSelecionado);
  }, [compras, fornecedorSelecionado]);

  const formatarValor = (valor) => {
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const converterDataParaComparacao = (dataString) => {
    const [dia, mes, ano] = dataString.split('/');
    return new Date(`${ano}-${mes}-${dia}`);
  };

  const filtrarCompras = () => {
    // Quando o botão filtrar é clicado, buscamos os dados da API
    // e a aplicação do filtro de fornecedor acontece no useEffect [compras, fornecedorSelecionado]
    buscarCompras();
  };

  const handleExportPDF = () => {
    //console.log("Exportando PDF...");
  };

  return (
    <div className={`historico-compra-pre-container ${isMenuExpanded ? 'menu-expanded' : ''}`}>
      <div className="historico-compra-pre-wrapper">
        <h1 className="historico-compra-pre-title">Histórico de Compras</h1>
        <div className="historico-compra-pre-content">
          <Grid
            container
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ mb: 2 }}
          >
            <Grid item xs={12} sm="auto">
              <TextField
                label="Data Início"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <TextField
                label="Data Fim"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <TextField
                select
                label="Fornecedor"
                fullWidth
                size="small"
                value={fornecedorSelecionado}
                onChange={(e) => setFornecedorSelecionado(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {[...new Set(compras.map(compra => compra.usuario?.USU_NOME).filter(nome => nome))].map((fornecedorNome, index) => (
                  <MenuItem key={index} value={fornecedorNome}>
                    {fornecedorNome}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                variant="contained"
                startIcon={<FilterAltIcon />}
                onClick={filtrarCompras}
                className="historico-compra-pre-filter-button"
                disabled={loading}
                fullWidth
                style={{ minWidth: 220, maxWidth: 300, height: 40 }}
              >
                {loading ? 'Carregando...' : 'Filtrar'}
              </Button>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleExportPDF}
                className="historico-compra-pre-export-button"
                disabled={loading}
                fullWidth
                style={{ minWidth: 220, maxWidth: 300, height: 40 }}
              >
                Exportar PDF
              </Button>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />

          {isMobile ? (
            <div>
              {loading ? (
                <Paper sx={{ p: 2, textAlign: 'center' }}>Carregando...</Paper>
              ) : comprasFiltradas.length > 0 ? (
                comprasFiltradas.map((compra) => (
                  <Paper key={compra.COM_ID} className="historico-compra-pre-card" elevation={2} sx={{ mb: 2, p: 2 }}>
                    <div><b>Nº:</b> {compra.COM_ID}</div>
                    <div>
                      <b>Itens:</b>
                      {compra.itens.map(item => (
                        <div key={item.IT_ID} style={{ marginLeft: 8 }}>
                          {item.IT_NOME} - {item.IT_DESCRICAO} ({item.IT_QUANTIDADE} un)
                        </div>
                      ))}
                    </div>
                    <div><b>Fornecedor:</b> {compra.usuario?.USU_NOME}</div>
                    <div><b>Data:</b> {new Date(compra.COM_DATA_VINCULACAO).toLocaleDateString('pt-BR')}</div>
                    <div><b>Status:</b> {compra.cotacao?.COT_STATUS}</div>
                  </Paper>
                ))
              ) : (
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  Nenhuma compra encontrada com os filtros aplicados
                </Paper>
              )}
            </div>
          ) : (
            <TableContainer component={Paper} className="historico-compra-pre-table-container">
              <Table stickyHeader size="small" className="historico-compra-pre-table">
                <TableHead>
                  <TableRow className="historico-compra-pre-table-header">
                    <TableCell>Nº</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Fornecedor</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Carregando...
                      </TableCell>
                    </TableRow>
                  ) : comprasFiltradas.length > 0 ? (
                    comprasFiltradas.map((compra) => (
                      <TableRow key={compra.COM_ID} hover>
                        <TableCell>{compra.COM_ID}</TableCell>
                        <TableCell>
                          {compra.itens.map(item => (
                            <div key={item.IT_ID}>
                              {item.IT_NOME} - {item.IT_DESCRICAO} ({item.IT_QUANTIDADE} un)
                            </div>
                          ))}
                        </TableCell>
                        <TableCell>{compra.usuario?.USU_NOME}</TableCell>
                        <TableCell>{new Date(compra.COM_DATA_VINCULACAO).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>{compra.cotacao?.COT_STATUS}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" className="historico-compra-pre-no-results">
                        Nenhuma compra encontrada com os filtros aplicados
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoricoCompra;